import React, { useRef, useCallback } from 'react';
import { Button, type ButtonProps } from '@mui/material';
import { gsap } from 'gsap';

export interface PopRotateButtonProps extends Omit<ButtonProps, 'variant'> {
  /** MUI button variant */
  muiVariant?: ButtonProps['variant'];
  /** Icon element to animate */
  icon?: React.ReactNode;
  /** Icon position */
  iconPosition?: 'start' | 'end';
  /** Children/button content */
  children: React.ReactNode;
}

/**
 * PopRotateButton - Elastic pop with rotation animation on click
 * 
 * Button scales on hover, icon pops and rotates on click with elastic bounce.
 * Perfect for favorite/like actions.
 * 
 * @example
 * ```tsx
 * <PopRotateButton icon={<HeartIcon />} iconPosition="start">
 *   Like
 * </PopRotateButton>
 * ```
 */
export function PopRotateButton({
  muiVariant = 'contained',
  icon,
  iconPosition = 'start',
  children,
  onClick,
  ...props
}: PopRotateButtonProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const iconRef = useRef<HTMLSpanElement>(null);

  const handleMouseEnter = useCallback(() => {
    if (buttonRef.current) {
      gsap.to(buttonRef.current, {
        scale: 1.05,
        duration: 0.3,
        ease: 'back.out(1.7)',
      });
    }
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (buttonRef.current) {
      gsap.to(buttonRef.current, {
        scale: 1,
        duration: 0.3,
        ease: 'power2.out',
      });
    }
  }, []);

  const handleClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    if (iconRef.current) {
      gsap.timeline()
        .to(iconRef.current, {
          scale: 1.4,
          rotation: 15,
          duration: 0.2,
          ease: 'back.out(2)',
        })
        .to(iconRef.current, {
          scale: 1,
          rotation: 0,
          duration: 0.3,
          ease: 'elastic.out(1, 0.5)',
        });
    }
    onClick?.(e);
  }, [onClick]);

  const iconElement = icon ? (
    <span 
      ref={iconRef}
      style={{ 
        display: 'inline-flex', 
        alignItems: 'center',
        willChange: 'transform',
      }}
    >
      {icon}
    </span>
  ) : null;

  return (
    <Button
      ref={buttonRef}
      variant={muiVariant}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
      {...props}
      sx={{
        willChange: 'transform',
        ...props.sx,
      }}
    >
      {iconPosition === 'start' && iconElement}
      {children}
      {iconPosition === 'end' && iconElement}
    </Button>
  );
}

export default PopRotateButton;
